(function(root, factory) {
  'use strict';

  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.EnigmaFreeShippingProgress = api;
})(typeof window !== 'undefined' ? window : null, function() {
  'use strict';

  var DEFAULT_NEAR_PERCENT = 80;
  var lastAnnouncementSignature = null;
  var announcementVersion = 0;
  var pendingAnnouncementFrame = null;
  var countryAnnouncementVersion = 0;
  var pendingCountryAnnouncementFrame = null;
  var boundCountryDocument = null;
  var activeCountryRoot = null;
  var countrySwitching = false;
  var latestCart = null;
  var disabledCheckoutControls = [];
  var CHECKOUT_SELECTOR = [
    '[data-cart-normal-checkout]',
    'button[name="checkout"]',
    'input[name="checkout"]',
    'button.cart__checkout',
    'a.checkout-button',
    '.additional-checkout-buttons'
  ].join(',');

  function asMinorUnits(value) {
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    if (typeof value === 'string' && !value.trim()) return null;
    var number = Number(value);
    if (!Number.isFinite(number) || number < 0 || number > Number.MAX_SAFE_INTEGER) return null;
    return Math.round(number);
  }

  function positiveMinorUnits(value) {
    var amount = asMinorUnits(value);
    return amount !== null && amount > 0 ? amount : null;
  }

  function parseCountryCodes(value) {
    var values = Array.isArray(value) ? value : String(value || '').split(',');
    return values.reduce(function(codes, code) {
      var normalized = String(code || '').trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(normalized) && codes.indexOf(normalized) === -1) codes.push(normalized);
      return codes;
    }, []);
  }

  function normalizeCountryPriceMap(value) {
    var source = value && typeof value === 'object' ? value : {};
    return Object.keys(source).reduce(function(prices, countryCode) {
      var normalizedCode = String(countryCode || '').trim().toUpperCase();
      var normalizedPrice = asMinorUnits(source[countryCode]);
      if (/^[A-Z]{2}$/.test(normalizedCode) && normalizedPrice !== null && normalizedPrice > 0) {
        prices[normalizedCode] = normalizedPrice;
      }
      return prices;
    }, {});
  }

  function eligibleSummary(cart) {
    var summary = { totalCents: 0, lineCount: 0, reliable: true };
    if (!cart || !Array.isArray(cart.items)) return summary;
    var allLinesTotal = 0;
    var usedFinalFallback = false;
    cart.items.forEach(function(item) {
      if (!item) { summary.reliable = false; return; }
      var eligible = item.requires_shipping === true && item.gift_card !== true;
      if (eligible) summary.lineCount += 1;
      // Ajax line_price includes allocated order discounts. final_line_price
      // only includes line-level discounts, so it is a guarded fallback.
      var amount = asMinorUnits(item.line_price);
      if (amount === null && !Object.prototype.hasOwnProperty.call(item, 'line_price')) {
        amount = asMinorUnits(item.final_line_price);
        usedFinalFallback = true;
      }
      if (amount === null) { summary.reliable = false; return; }
      allLinesTotal += amount;
      if (eligible) summary.totalCents += amount;
    });
    var cartTotal = asMinorUnits(cart.total_price);
    if (Object.prototype.hasOwnProperty.call(cart, 'total_price') && cartTotal === null) summary.reliable = false;
    if (cartTotal !== null && cartTotal !== allLinesTotal) summary.reliable = false;
    if (usedFinalFallback && Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length) {
      summary.reliable = false;
    }
    // Native threshold QA confirmed tax-inclusive physical cart prices are
    // used by Shopify's shipping minimum. Do not deduct embedded VAT again.
    if (!Number.isSafeInteger(allLinesTotal) || !Number.isSafeInteger(summary.totalCents)) summary.reliable = false;
    return summary;
  }

  function normalizedConfig(config) {
    config = config || {};
    return {
      itEnabled: config.itEnabled === true,
      itThresholdCents: asMinorUnits(config.itThresholdCents),
      euEnabled: config.euEnabled === true,
      euThresholdCents: asMinorUnits(config.euThresholdCents),
      euCountryCodes: parseCountryCodes(config.euCountryCodes),
      itShippingStartCents: positiveMinorUnits(config.itShippingStartCents),
      euShippingStartCents: normalizeCountryPriceMap(config.euShippingStartCents),
      countryCode: String(config.countryCode || '').trim().toUpperCase(),
      countryName: String(config.countryName || '').trim(),
      currency: String(config.currency || 'EUR').trim().toUpperCase(),
      presentmentCurrency: String(config.presentmentCurrency || '').trim().toUpperCase(),
      currencyRate: (typeof config.currencyRate === 'number' || typeof config.currencyRate === 'string') &&
        String(config.currencyRate).trim() && Number.isFinite(Number(config.currencyRate)) && Number(config.currencyRate) > 0
        ? Number(config.currencyRate) : null,
      locale: String(config.locale || '').trim(),
      nearPercent: positiveMinorUnits(config.nearPercent) !== null
        ? Math.min(99, Math.max(1, Number(config.nearPercent)))
        : DEFAULT_NEAR_PERCENT,
      messages: config.messages || {}
    };
  }

  function resolveOffer(cart, config) {
    var normalized = normalizedConfig(config);
    var cartCurrency = String(cart && cart.currency || '').trim().toUpperCase();
    var region = normalized.countryCode === 'IT' && normalized.itEnabled && normalized.itThresholdCents > 0 ? 'it' : null;
    if (!region &&
      normalized.euEnabled &&
      normalized.euThresholdCents > 0 &&
      normalized.euCountryCodes.indexOf(normalized.countryCode) !== -1
    ) region = 'eu';
    if (!region) return null;
    var baseThreshold = region === 'it' ? normalized.itThresholdCents : normalized.euThresholdCents;
    var baseShippingStart = region === 'it' ? normalized.itShippingStartCents : normalized.euShippingStartCents[normalized.countryCode];
    var sameCurrency = cartCurrency === normalized.currency;
    var staleCurrency = normalized.presentmentCurrency && normalized.presentmentCurrency !== cartCurrency;
    var canConvert = normalized.currency === 'EUR' && !sameCurrency && !staleCurrency &&
      normalized.presentmentCurrency === cartCurrency && normalized.currencyRate !== null;
    // Shopify's public runtime FX is an estimate here, not a shipping minimum
    // contract. Round the estimated threshold upwards to avoid an early cent.
    var threshold = sameCurrency && !staleCurrency ? baseThreshold :
      (canConvert ? Math.ceil(baseThreshold * normalized.currencyRate) : null);
    if (!Number.isSafeInteger(threshold) || threshold <= 0) threshold = null;
    var shippingStart = threshold === null || !baseShippingStart ? null :
      (sameCurrency ? baseShippingStart : asMinorUnits(baseShippingStart * normalized.currencyRate));
    return {
      region: region,
      countryCode: normalized.countryCode,
      currency: cartCurrency || normalized.currency,
      baseCurrency: normalized.currency,
      baseThresholdCents: baseThreshold,
      thresholdCents: threshold,
      shippingStartCents: shippingStart,
      estimated: canConvert && threshold !== null,
      nearPercent: normalized.nearPercent,
      locale: normalized.locale,
      countryName: normalized.countryName || normalized.countryCode,
      messages: normalized.messages[region] || {},
      commonMessages: normalized.messages,
      shippingMessages: normalized.messages.shipping || {}
    };
  }

  function formatMoney(cents, currency, locale) {
    var amount = asMinorUnits(cents);
    if (amount === null) return '';
    try {
      return new Intl.NumberFormat(locale || undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount / 100);
    } catch (error) {
      return currency + ' ' + (amount / 100).toFixed(2);
    }
  }

  function messageFromTemplate(template, amount) {
    return String(template || '').split('__AMOUNT__').join(amount);
  }

  function shippingMessageFromTemplate(template, amount, countryName) {
    return String(template || '')
      .split('__AMOUNT__').join(amount)
      .split('__COUNTRY__').join(countryName);
  }

  function contextualMessage(offer, key) {
    return shippingMessageFromTemplate(
      offer.commonMessages[key],
      formatMoney(offer.baseThresholdCents, offer.baseCurrency, offer.locale),
      offer.countryName
    );
  }

  function qualifiedMessage(message, offer, unknownBasis) {
    var parts = [message];
    var conciseItaly = offer.countryCode === 'IT' && /^it(?:-|$)/i.test(offer.locale) &&
      offer.thresholdCents !== null && !offer.estimated && !unknownBasis;
    if (!conciseItaly) parts.push(contextualMessage(offer, 'qualifier'));
    if (offer.countryCode === 'ES') parts.push(contextualMessage(offer, 'esExclusions'));
    if (offer.countryCode === 'PT') parts.push(contextualMessage(offer, 'ptExclusions'));
    if (offer.estimated && !unknownBasis) parts.push(contextualMessage(offer, 'estimateNote'));
    if (unknownBasis) parts.push(contextualMessage(offer, 'unavailable'));
    return parts.filter(function(part) { return Boolean(part); }).join(' ');
  }

  function createViewModel(cart, config) {
    if (!cart || Number(cart.item_count) <= 0) return null;

    var offer = resolveOffer(cart, config);
    if (!offer) return null;

    var eligible = eligibleSummary(cart);
    if (eligible.lineCount === 0) return null;

    var threshold = offer.thresholdCents;
    if (threshold === null || !eligible.reliable || (offer.estimated && !offer.commonMessages.estimateNote)) {
      var fallback = contextualMessage(offer, 'policyFallback');
      if (!fallback) return null;
      return {
        region: offer.region,
        countryCode: offer.countryCode,
        currency: offer.currency,
        state: 'informational',
        estimated: false,
        eligibleCents: null,
        thresholdCents: null,
        baseThresholdCents: offer.baseThresholdCents,
        remainingCents: null,
        visualPercent: null,
        ariaPercent: null,
        shippingStartCents: null,
        shippingMessage: '',
        message: qualifiedMessage(fallback, offer, !eligible.reliable)
      };
    }
    var remaining = Math.max(0, threshold - eligible.totalCents);
    var achieved = eligible.totalCents >= threshold;
    var exactPercent = threshold > 0 ? (eligible.totalCents / threshold) * 100 : 0;
    var state = achieved ? 'achieved' : (exactPercent >= offer.nearPercent ? 'near' : 'below');
    var visualPercent = achieved ? 100 : Math.min(99.5, Math.max(0, exactPercent));
    var ariaPercent = achieved ? 100 : Math.min(99, Math.max(0, Math.floor(exactPercent)));
    var template = offer.messages[state];
    if (!template) return null;
    var shippingTemplate = achieved
      ? offer.shippingMessages.free
      : offer.shippingMessages.paid;
    var shippingAmount = offer.shippingStartCents === null || offer.shippingStartCents === undefined
      ? ''
      : formatMoney(offer.shippingStartCents, offer.currency, offer.locale);
    var shippingMessage = '';
    if (shippingTemplate && (achieved || shippingAmount)) {
      shippingMessage = shippingMessageFromTemplate(
        shippingTemplate,
        shippingAmount,
        offer.countryName
      );
    }

    return {
      region: offer.region,
      countryCode: offer.countryCode,
      currency: offer.currency,
      state: state,
      estimated: offer.estimated,
      eligibleCents: eligible.totalCents,
      thresholdCents: threshold,
      baseThresholdCents: offer.baseThresholdCents,
      remainingCents: remaining,
      visualPercent: visualPercent,
      ariaPercent: ariaPercent,
      shippingStartCents: offer.shippingStartCents,
      shippingMessage: shippingMessage,
      message: qualifiedMessage(shippingMessageFromTemplate(
        template,
        formatMoney(remaining, offer.currency, offer.locale),
        offer.countryName
      ), offer, false)
    };
  }

  function createSurfaceState(cart, config) {
    return {
      visible: Boolean(cart && Number(cart.item_count) > 0 && eligibleSummary(cart).lineCount > 0),
      offer: createViewModel(cart, config)
    };
  }

  function readBoolean(root, name) {
    return root.getAttribute(name) === 'true';
  }

  function readConfig(root) {
    var windowRef = root.ownerDocument && root.ownerDocument.defaultView;
    var runtimeCurrency = windowRef && windowRef.Shopify && windowRef.Shopify.currency || {};
    return {
      itEnabled: readBoolean(root, 'data-enigma-free-shipping-it-enabled'),
      itThresholdCents: root.getAttribute('data-enigma-free-shipping-it-threshold'),
      euEnabled: readBoolean(root, 'data-enigma-free-shipping-eu-enabled'),
      euThresholdCents: root.getAttribute('data-enigma-free-shipping-eu-threshold'),
      euCountryCodes: root.getAttribute('data-enigma-free-shipping-eu-countries'),
      itShippingStartCents: root.getAttribute('data-enigma-shipping-estimate-it-start'),
      euShippingStartCents: {
        AT: root.getAttribute('data-enigma-shipping-estimate-eu-r-start'),
        DE: root.getAttribute('data-enigma-shipping-estimate-eu-r-start'),
        FR: root.getAttribute('data-enigma-shipping-estimate-eu-r-start'),
        SI: root.getAttribute('data-enigma-shipping-estimate-eu-r-start'),
        BE: root.getAttribute('data-enigma-shipping-estimate-eu-s-start'),
        ES: root.getAttribute('data-enigma-shipping-estimate-eu-s-start'),
        LU: root.getAttribute('data-enigma-shipping-estimate-eu-s-start'),
        NL: root.getAttribute('data-enigma-shipping-estimate-eu-s-start'),
        PT: root.getAttribute('data-enigma-shipping-estimate-eu-s-start'),
        BG: root.getAttribute('data-enigma-shipping-estimate-eu-t-start'),
        CZ: root.getAttribute('data-enigma-shipping-estimate-eu-t-start'),
        HU: root.getAttribute('data-enigma-shipping-estimate-eu-t-start'),
        PL: root.getAttribute('data-enigma-shipping-estimate-eu-t-start'),
        RO: root.getAttribute('data-enigma-shipping-estimate-eu-t-start'),
        SK: root.getAttribute('data-enigma-shipping-estimate-eu-t-start'),
        DK: root.getAttribute('data-enigma-shipping-estimate-eu-t-start'),
        GR: root.getAttribute('data-enigma-shipping-estimate-eu-t-start'),
        HR: root.getAttribute('data-enigma-shipping-estimate-eu-t-start')
      },
      countryCode: root.getAttribute('data-enigma-free-shipping-country'),
      countryName: root.getAttribute('data-enigma-shipping-country-name'),
      currency: root.getAttribute('data-enigma-free-shipping-currency') || 'EUR',
      presentmentCurrency: runtimeCurrency.active,
      currencyRate: runtimeCurrency.rate,
      locale: root.getAttribute('data-enigma-free-shipping-locale'),
      nearPercent: root.getAttribute('data-enigma-free-shipping-near-percent'),
      messages: {
        qualifier: root.getAttribute('data-enigma-free-shipping-qualifier'),
        estimateNote: root.getAttribute('data-enigma-free-shipping-estimate-note'),
        policyFallback: root.getAttribute('data-enigma-free-shipping-policy-fallback'),
        unavailable: root.getAttribute('data-enigma-free-shipping-unavailable'),
        esExclusions: root.getAttribute('data-enigma-free-shipping-es-exclusions'),
        ptExclusions: root.getAttribute('data-enigma-free-shipping-pt-exclusions'),
        it: {
          below: root.getAttribute('data-enigma-free-shipping-it-below'),
          near: root.getAttribute('data-enigma-free-shipping-it-near'),
          achieved: root.getAttribute('data-enigma-free-shipping-it-achieved')
        },
        eu: {
          below: root.getAttribute('data-enigma-free-shipping-eu-below'),
          near: root.getAttribute('data-enigma-free-shipping-eu-near'),
          achieved: root.getAttribute('data-enigma-free-shipping-eu-achieved')
        },
        shipping: {
          paid: root.getAttribute('data-enigma-shipping-estimate-paid'),
          free: root.getAttribute('data-enigma-shipping-estimate-free')
        }
      }
    };
  }

  function instanceForContext(documentRef, context) {
    return documentRef.querySelector(
      '[data-enigma-free-shipping-progress][data-enigma-free-shipping-context="' + context + '"]'
    );
  }

  function firstCheckoutAction(target) {
    return Array.prototype.find.call(target.children, function(child) {
      return child.matches && child.matches('.enigma-cart-drawer-actions, .btn_submit, a.btn, button.cart__checkout');
    }) || null;
  }

  function surfaceTargets(documentRef) {
    var pageTarget = documentRef.querySelector('.cart__layout_right .grid > .grid__item');
    var drawerSlot = documentRef.querySelector('#cart-info [data-enigma-drawer-shipping-slot]');
    var drawerTarget = drawerSlot || documentRef.querySelector('#cart-info .ajaxcart__footer');
    var popupTarget = documentRef.querySelector('.cart__popup .cart__popup_content');
    return [
      { context: 'page', target: pageTarget, before: null },
      { context: 'drawer', target: drawerTarget, before: !drawerSlot && drawerTarget ? firstCheckoutAction(drawerTarget) : null },
      { context: 'popup', target: popupTarget, before: null }
    ];
  }

  function ensureInstances(root, documentRef) {
    var template = root.querySelector('template[data-enigma-free-shipping-template]');
    if (!template || !template.content) return [];

    return surfaceTargets(documentRef).reduce(function(instances, surface) {
      if (!surface.target) return instances;

      var instance = instanceForContext(documentRef, surface.context);
      if (!instance) {
        var fragment = template.content.cloneNode(true);
        instance = fragment.querySelector('[data-enigma-free-shipping-progress]');
        if (!instance) return instances;
        instance.setAttribute('data-enigma-free-shipping-context', surface.context);
        instance.classList.add('enigma-free-shipping-progress--' + surface.context);
        if (surface.context === 'drawer') {
          var countryControl = instance.querySelector('[data-enigma-shipping-country-control]');
          if (countryControl) countryControl.remove();
        }
        surface.target.insertBefore(fragment, surface.before);
      }
      instances.push(instance);
      return instances;
    }, []);
  }

  function hideInstances(documentRef) {
    documentRef.querySelectorAll('[data-enigma-free-shipping-progress]').forEach(function(instance) {
      instance.hidden = true;
    });
  }

  function countryOption(select, value) {
    var code = String(value || '').trim().toUpperCase();
    if (!select || !/^[A-Z]{2}$/.test(code)) return null;
    return Array.prototype.find.call(select.options || [], function(option) {
      return String(option.value || '').trim() === code;
    }) || null;
  }

  function syncCountryPicker(instance, countryCode) {
    var select = instance.querySelector('[data-enigma-shipping-country-select]');
    var button = instance.querySelector('[data-enigma-shipping-country-apply]');
    if (!select) return;

    var appliedOption = countryOption(select, countryCode);
    var previousApplied = select.getAttribute('data-enigma-shipping-applied-code');
    var selectedOption = countryOption(select, select.value);
    if (!selectedOption || previousApplied !== countryCode) {
      if (appliedOption) select.value = appliedOption.value;
      selectedOption = appliedOption;
    }

    var cartMutating = instance.ownerDocument.documentElement.hasAttribute('data-cart-mutating');
    select.setAttribute('data-enigma-shipping-applied-code', countryCode);
    select.disabled = countrySwitching || cartMutating || !appliedOption;
    if (button) {
      button.disabled = countrySwitching || cartMutating || !appliedOption || !selectedOption || selectedOption.value === appliedOption.value;
    }
  }

  function clearOffer(instance, offerElement) {
    offerElement.hidden = true;
    instance.setAttribute('data-state', 'ineligible');
    instance.removeAttribute('data-enigma-free-shipping-region');
    instance.removeAttribute('data-enigma-free-shipping-eligible-cents');
    instance.removeAttribute('data-enigma-free-shipping-threshold-cents');
    instance.removeAttribute('data-enigma-free-shipping-estimated');
    instance.style.setProperty('--enigma-free-shipping-progress-value', '0%');

    var message = instance.querySelector('[data-enigma-free-shipping-message]');
    var bar = instance.querySelector('[data-enigma-free-shipping-bar]');
    if (message) message.textContent = '';
    if (bar) {
      bar.hidden = true;
      bar.setAttribute('aria-valuenow', '0');
      bar.setAttribute('aria-valuetext', '');
    }
    var shippingCost = instance.querySelector('[data-enigma-shipping-cost]');
    if (shippingCost) {
      shippingCost.textContent = '';
      shippingCost.hidden = true;
    }
  }

  function updateInstance(instance, surfaceState, countryCode) {
    var drawerCost = instance.getAttribute('data-enigma-free-shipping-context') === 'drawer'
      ? instance.ownerDocument.querySelector('[data-enigma-drawer-shipping-cost]') : null;
    if (drawerCost) drawerCost.hidden = true;
    if (!surfaceState.visible) {
      instance.hidden = true;
      return;
    }

    instance.hidden = false;
    syncCountryPicker(instance, countryCode);

    var countrySelect = instance.querySelector('[data-enigma-shipping-country-select]');
    var offerElement = instance.querySelector('[data-enigma-free-shipping-offer]');
    if (!offerElement) {
      if (!countrySelect) instance.hidden = true;
      return;
    }
    var viewModel = surfaceState.offer;
    if (countrySwitching || !viewModel) {
      clearOffer(instance, offerElement);
      if (!countrySelect) instance.hidden = true;
      return;
    }

    var message = instance.querySelector('[data-enigma-free-shipping-message]');
    var bar = instance.querySelector('[data-enigma-free-shipping-bar]');
    if (!message || !bar) {
      clearOffer(instance, offerElement);
      return;
    }

    offerElement.hidden = false;
    instance.setAttribute('data-state', viewModel.state);
    instance.setAttribute('data-enigma-free-shipping-region', viewModel.region);
    instance.setAttribute('data-enigma-free-shipping-estimated', viewModel.estimated ? 'true' : 'false');
    var informational = viewModel.state === 'informational';
    if (informational) {
      instance.removeAttribute('data-enigma-free-shipping-eligible-cents');
      instance.removeAttribute('data-enigma-free-shipping-threshold-cents');
    } else {
      instance.setAttribute('data-enigma-free-shipping-eligible-cents', String(viewModel.eligibleCents));
      instance.setAttribute('data-enigma-free-shipping-threshold-cents', String(viewModel.thresholdCents));
    }
    instance.style.setProperty(
      '--enigma-free-shipping-progress-value',
      informational ? '0%' : viewModel.visualPercent.toFixed(2) + '%'
    );
    var compactTemplate = instance.getAttribute('data-enigma-short-it');
    var compactMessage = drawerCost && compactTemplate && viewModel.countryCode === 'IT'
      && !viewModel.estimated && viewModel.currency === 'EUR'
      && (viewModel.state === 'below' || viewModel.state === 'near')
      ? compactTemplate.replace('__AMOUNT__', formatMoney(viewModel.remainingCents, viewModel.currency, instance.getAttribute('data-enigma-locale')))
      : viewModel.message;
    message.textContent = compactMessage;
    if (drawerCost) {
      if (drawerCost.textContent !== (viewModel.shippingMessage || '')) drawerCost.textContent = viewModel.shippingMessage || '';
      drawerCost.hidden = !viewModel.shippingMessage;
    }
    var shippingCost = instance.querySelector('[data-enigma-shipping-cost]');
    if (shippingCost) {
      shippingCost.textContent = viewModel.shippingMessage || '';
      shippingCost.hidden = !viewModel.shippingMessage;
    }
    bar.hidden = informational;
    if (informational) bar.removeAttribute('aria-valuenow');
    else bar.setAttribute('aria-valuenow', String(viewModel.ariaPercent));
    bar.setAttribute('aria-valuetext', viewModel.message);
  }

  function announceCountry(root, type) {
    if (!root) return;
    var liveRegion = root.querySelector('[data-enigma-shipping-country-live]');
    if (!liveRegion) return;
    var windowRef = root.ownerDocument && root.ownerDocument.defaultView;
    var attribute = type === 'updating'
      ? 'data-enigma-shipping-country-updating'
      : 'data-enigma-shipping-country-error';
    var message = liveRegion.getAttribute(attribute) || '';

    countryAnnouncementVersion += 1;
    var version = countryAnnouncementVersion;
    if (
      pendingCountryAnnouncementFrame !== null &&
      windowRef &&
      typeof windowRef.cancelAnimationFrame === 'function'
    ) {
      windowRef.cancelAnimationFrame(pendingCountryAnnouncementFrame);
    }
    pendingCountryAnnouncementFrame = null;
    liveRegion.textContent = '';

    var publish = function() {
      if (version !== countryAnnouncementVersion) return;
      pendingCountryAnnouncementFrame = null;
      liveRegion.textContent = message;
    };
    if (windowRef && typeof windowRef.requestAnimationFrame === 'function') {
      pendingCountryAnnouncementFrame = windowRef.requestAnimationFrame(publish);
    } else {
      publish();
    }
  }

  function lockCheckoutControls(documentRef) {
    documentRef.querySelectorAll(CHECKOUT_SELECTOR).forEach(function(control) {
      var known = disabledCheckoutControls.some(function(entry) {
        return entry.element === control;
      });
      if (known) return;

      var supportsDisabled = 'disabled' in control;
      disabledCheckoutControls.push({
        element: control,
        supportsDisabled: supportsDisabled,
        disabled: supportsDisabled ? control.disabled : false,
        ariaDisabled: control.getAttribute('aria-disabled'),
        hadInert: control.hasAttribute('inert')
      });
      if (supportsDisabled) control.disabled = true;
      control.setAttribute('aria-disabled', 'true');
      control.setAttribute('inert', '');
    });
  }

  function unlockCheckoutControls() {
    disabledCheckoutControls.forEach(function(entry) {
      var control = entry.element;
      if (!control || control.isConnected === false) return;
      if (entry.supportsDisabled) control.disabled = entry.disabled;
      if (entry.ariaDisabled === null) control.removeAttribute('aria-disabled');
      else control.setAttribute('aria-disabled', entry.ariaDisabled);
      if (!entry.hadInert) control.removeAttribute('inert');
    });
    disabledCheckoutControls = [];
  }

  function setCountrySwitching(documentRef, isSwitching) {
    countrySwitching = isSwitching;
    documentRef.documentElement.toggleAttribute('data-enigma-country-switching', isSwitching);
    documentRef.querySelectorAll('[data-enigma-free-shipping-progress]').forEach(function(instance) {
      if (isSwitching) {
        instance.setAttribute('aria-busy', 'true');
        var offer = instance.querySelector('[data-enigma-free-shipping-offer]');
        if (offer) clearOffer(instance, offer);
        instance.querySelectorAll('[data-enigma-shipping-country-select], [data-enigma-shipping-country-apply]').forEach(function(control) {
          control.disabled = true;
        });
      } else {
        instance.removeAttribute('aria-busy');
      }
    });
    if (isSwitching) lockCheckoutControls(documentRef);
    else unlockCheckoutControls();
  }

  function closestMatching(target, selector) {
    if (!target) return null;
    if (typeof target.closest === 'function') return target.closest(selector);
    return target.matches && target.matches(selector) ? target : null;
  }

  function handleCountryChange(event) {
    var select = closestMatching(event.target, '[data-enigma-shipping-country-select]');
    if (!select || !activeCountryRoot || countrySwitching) return;

    var appliedCode = normalizedConfig(readConfig(activeCountryRoot)).countryCode;
    var selectedOption = countryOption(select, select.value);
    var appliedOption = countryOption(select, appliedCode);
    var instance = closestMatching(select, '[data-enigma-free-shipping-progress]');
    var button = instance && instance.querySelector('[data-enigma-shipping-country-apply]');

    if (!selectedOption || !appliedOption) {
      if (appliedOption) select.value = appliedOption.value;
      if (button) button.disabled = true;
      announceCountry(activeCountryRoot, 'error');
      return;
    }

    if (button) button.disabled = selectedOption.value === appliedOption.value;
  }

  function restoreAfterCountrySubmitError(documentRef) {
    setCountrySwitching(documentRef, false);
    if (activeCountryRoot && latestCart) update(latestCart, activeCountryRoot);
    announceCountry(activeCountryRoot, 'error');
  }

  function scheduleCountrySubmit(form, documentRef) {
    var windowRef = documentRef.defaultView;
    var submit = function() {
      try {
        form.submit();
      } catch (error) {
        restoreAfterCountrySubmitError(documentRef);
      }
    };

    if (windowRef && typeof windowRef.requestAnimationFrame === 'function') {
      windowRef.requestAnimationFrame(function() {
        windowRef.requestAnimationFrame(submit);
      });
    } else if (windowRef && typeof windowRef.setTimeout === 'function') {
      windowRef.setTimeout(submit, 0);
    } else {
      submit();
    }
  }

  function handleCountryApply(event) {
    var button = closestMatching(event.target, '[data-enigma-shipping-country-apply]');
    if (!button || !activeCountryRoot) return;
    event.preventDefault();
    if (countrySwitching) return;

    var documentRef = activeCountryRoot.ownerDocument;
    if (documentRef.documentElement.hasAttribute('data-cart-mutating')) {
      button.disabled = true;
      return;
    }

    var instance = closestMatching(button, '[data-enigma-free-shipping-progress]');
    var select = instance && instance.querySelector('[data-enigma-shipping-country-select]');
    var selectedOption = countryOption(select, select && select.value);
    var appliedCode = normalizedConfig(readConfig(activeCountryRoot)).countryCode;
    var appliedOption = countryOption(select, appliedCode);
    var form = documentRef.getElementById('EnigmaShippingCountryForm');
    var countryInput = form && form.querySelector('[data-enigma-shipping-country-input]');

    if (!selectedOption || !appliedOption || !form || !countryInput) {
      announceCountry(activeCountryRoot, 'error');
      return;
    }
    if (selectedOption.value === appliedOption.value) {
      button.disabled = true;
      return;
    }

    countryInput.value = selectedOption.value;
    setCountrySwitching(documentRef, true);
    updateLiveRegion(activeCountryRoot, null);
    announceCountry(activeCountryRoot, 'updating');
    scheduleCountrySubmit(form, documentRef);
  }

  function preventCheckoutWhileCountrySwitching(event) {
    if (!countrySwitching || !closestMatching(event.target, CHECKOUT_SELECTOR)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function restoreCountryControlsOnPageShow(event) {
    if (!countrySwitching || !activeCountryRoot) return;
    var documentRef = activeCountryRoot.ownerDocument;
    var windowRef = documentRef.defaultView;
    if (event && event.persisted && windowRef && windowRef.location) {
      windowRef.location.reload();
      return;
    }
    setCountrySwitching(documentRef, false);
    if (latestCart) update(latestCart, activeCountryRoot);
  }

  function bindCountryControls(root) {
    activeCountryRoot = root;
    var documentRef = root.ownerDocument;
    if (boundCountryDocument === documentRef) return;
    boundCountryDocument = documentRef;
    documentRef.addEventListener('change', handleCountryChange);
    documentRef.addEventListener('click', handleCountryApply);
    documentRef.addEventListener('click', preventCheckoutWhileCountrySwitching, true);
    if (documentRef.defaultView) {
      documentRef.defaultView.addEventListener('pageshow', restoreCountryControlsOnPageShow);
    }
  }

  function updateLiveRegion(root, viewModel) {
    var signature = viewModel
      ? [viewModel.countryCode, viewModel.currency, viewModel.state, viewModel.eligibleCents,
        viewModel.thresholdCents, viewModel.remainingCents, viewModel.message].join(':')
      : 'hidden';
    var liveRegion = root.querySelector('[data-enigma-free-shipping-live]');
    var windowRef = root.ownerDocument && root.ownerDocument.defaultView;

    if (lastAnnouncementSignature === null) {
      lastAnnouncementSignature = signature;
      if (!viewModel && liveRegion) liveRegion.textContent = '';
      return;
    }
    if (signature === lastAnnouncementSignature) return;

    lastAnnouncementSignature = signature;
    announcementVersion += 1;
    var version = announcementVersion;

    if (
      pendingAnnouncementFrame !== null &&
      windowRef &&
      typeof windowRef.cancelAnimationFrame === 'function'
    ) {
      windowRef.cancelAnimationFrame(pendingAnnouncementFrame);
    }
    pendingAnnouncementFrame = null;

    if (!liveRegion) return;
    liveRegion.textContent = '';
    if (!viewModel) return;

    var publish = function() {
      if (version !== announcementVersion || signature !== lastAnnouncementSignature) return;
      pendingAnnouncementFrame = null;
      liveRegion.textContent = viewModel.message;
    };
    if (windowRef && typeof windowRef.requestAnimationFrame === 'function') {
      pendingAnnouncementFrame = windowRef.requestAnimationFrame(publish);
    } else {
      publish();
    }
  }

  function update(cart, root) {
    if (!root || !root.ownerDocument) return null;
    var documentRef = root.ownerDocument;
    var config = readConfig(root);
    var normalized = normalizedConfig(config);
    var surfaceState = createSurfaceState(cart, config);
    latestCart = cart;
    bindCountryControls(root);

    if (!surfaceState.visible) {
      hideInstances(documentRef);
      updateLiveRegion(root, null);
      return null;
    }

    if (countrySwitching) lockCheckoutControls(documentRef);
    ensureInstances(root, documentRef).forEach(function(instance) {
      updateInstance(instance, surfaceState, normalized.countryCode);
    });
    updateLiveRegion(root, countrySwitching ? null : surfaceState.offer);
    return surfaceState.offer;
  }

  return {
    asMinorUnits: asMinorUnits,
    parseCountryCodes: parseCountryCodes,
    normalizeCountryPriceMap: normalizeCountryPriceMap,
    eligibleSummary: eligibleSummary,
    normalizedConfig: normalizedConfig,
    resolveOffer: resolveOffer,
    formatMoney: formatMoney,
    shippingMessageFromTemplate: shippingMessageFromTemplate,
    createViewModel: createViewModel,
    createSurfaceState: createSurfaceState,
    countryOption: countryOption,
    readConfig: readConfig,
    updateLiveRegion: updateLiveRegion,
    update: update
  };
});
